// material-ui
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Stack,
  Popper,
  IconButton,
  List,
  ListItemButton,
  Paper,
  ListItemText,
  Typography,
  Divider,
  ClickAwayListener
} from '@mui/material';
import LogoSection from 'layout/MainLayout/LogoSection';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LanguageButton from 'ui-component/LanguageButton';
import ProfileSection from 'layout/MainLayout/Header/ProfileSection';
import { IconMenu2 } from '@tabler/icons-react';
import Transitions from 'ui-component/extended/Transitions';
import MainCard from 'ui-component/cards/MainCard';
import { useMediaQuery } from '@mui/material';

const GOLD = '#c9a84c';

// Active nav link style
const activeLinkSx = {
  color: GOLD,
  fontWeight: 600,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    right: 0,
    height: '2px',
    background: GOLD,
    borderRadius: '1px'
  }
};

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const account = useSelector((state) => state.account);
  const [open, setOpen] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenMenu = (event) => {
    setOpen(open ? null : event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const navLinks = [
    { to: '/', label: '首页' },
    { to: '/pricing', label: '定价' },
    { to: '/about', label: '关于' }
  ];

  return (
    <>
      <Box
        sx={{
          width: 228,
          display: 'flex',
          [theme.breakpoints.down('md')]: {
            width: 'auto'
          }
        }}
      >
        <Box component="span" sx={{ flexGrow: 1 }}>
          <LogoSection />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />
      <Stack spacing={2} direction="row" justifyContent="center" alignItems="center">
        {isMobile ? (
          <>
            <LanguageButton />
            <IconButton onClick={handleOpenMenu} sx={{ color: '#A8A29E' }}>
              <IconMenu2 />
            </IconButton>
          </>
        ) : (
          <>
            {navLinks.map(({ to, label }) => (
              <Button
                key={to}
                component={Link}
                variant="text"
                to={to}
                sx={{
                  fontSize: 14,
                  fontWeight: pathname === to ? 600 : 500,
                  color: pathname === to ? GOLD : '#A8A29E',
                  position: 'relative',
                  minWidth: 'unset',
                  px: 1,
                  '&:hover': { color: '#FAFAF9', bgcolor: 'transparent' },
                  transition: 'color 0.2s',
                  ...(pathname === to ? activeLinkSx : {})
                }}
              >
                {label}
              </Button>
            ))}
            <LanguageButton />
            {account.user ? (
              <>
                <Button
                  component={Link}
                  variant="contained"
                  to="/panel"
                  sx={{
                    background: `linear-gradient(135deg, ${GOLD} 0%, #8b6520 100%)`,
                    color: '#111010',
                    fontWeight: 700,
                    borderRadius: '8px',
                    px: 2.5,
                    py: 0.9,
                    fontSize: 14,
                    boxShadow: `0 4px 14px rgba(201,168,76,0.35)`,
                    '&:hover': {
                      boxShadow: `0 6px 20px rgba(201,168,76,0.5)`,
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  控制台
                </Button>
                <ProfileSection />
              </>
            ) : (
              <Button
                component={Link}
                variant="contained"
                to="/login"
                sx={{
                  background: `linear-gradient(135deg, ${GOLD} 0%, #8b6520 100%)`,
                  color: '#111010',
                  fontWeight: 700,
                  borderRadius: '8px',
                  px: 2.5,
                  py: 0.9,
                  fontSize: 14,
                  boxShadow: `0 4px 14px rgba(201,168,76,0.35)`,
                  '&:hover': {
                    boxShadow: `0 6px 20px rgba(201,168,76,0.5)`,
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                登录
              </Button>
            )}
          </>
        )}
      </Stack>

      <Popper
        open={!!open}
        anchorEl={open}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
        style={{ width: '100vw' }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleCloseMenu}>
              <Paper
                style={{
                  width: '100%',
                  background: '#1C1917',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <List
                    component="nav"
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: '100%',
                      backgroundColor: '#1C1917',
                      '& .MuiListItemButton-root': {
                        mt: 0.5,
                        color: '#A8A29E',
                        '&:hover': { color: '#FAFAF9', bgcolor: 'rgba(255,255,255,0.05)' }
                      }
                    }}
                    onClick={handleCloseMenu}
                  >
                    {navLinks.map(({ to, label }) => (
                      <ListItemButton key={to} component={Link} to={to}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: pathname === to ? GOLD : 'inherit' }}>
                              {label}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    {account.user ? (
                      <ListItemButton component={Link} to="/panel">
                        <ListItemText primary={<Typography variant="body2" sx={{ color: GOLD, fontWeight: 600 }}>控制台</Typography>} />
                      </ListItemButton>
                    ) : (
                      <ListItemButton component={Link} to="/login">
                        <ListItemText primary={<Typography variant="body2" sx={{ color: GOLD, fontWeight: 600 }}>登录</Typography>} />
                      </ListItemButton>
                    )}
                  </List>
                </MainCard>
              </Paper>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default Header;
